import { DatePipe } from '@angular/common';
import {
    currencyFormatter,
    formatCurrencyCompact,
    formatNumber,
    formatNumbersCompact,
    normalizeNumberInput,
    percentageFormatter
} from '@utils/format-functions';
import { ValueFormatterParams } from 'ag-grid-enterprise';

export type NullableNumber = number | string | null | undefined;

/**
 * Formats a numeric value as USD currency using the shared currencyFormatter.
 * Intended to be reused across different grids.
 */
export function formatCurrencyUSD(value: NullableNumber): string {
    const numeric = normalizeNumberInput(value);
    if (numeric === null) return '';
    return currencyFormatter(numeric);
}

/**
 * Formats a numeric value as percentage using the existing percentageFormatter.
 */
export function formatPercentageDisplay(value: NullableNumber): string {
    if (value == null) return '';
    const formatted = percentageFormatter(Number(value));
    return formatted ?? '';
}

/**
 * Formats a generic number for plain display without special formatting.
 */
export function formatNumberDisplay(value: NullableNumber): string {
    if (value == null) {
        return '';
    }

    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
        return '';
    }

    return formatNumber(numeric);
}

/**
 * Formats a numeric value as compact USD currency (e.g. 1.2K, 3M).
 */
export function formatCurrencyUSDCompact(value: NullableNumber): string {
    if (value == null) {
        return '';
    }

    return formatCurrencyCompact(String(value));
}

/**
 * Formats a numeric value as compact number (e.g. 1.2K, 3M).
 */
export function formatNumberCompact(value: NullableNumber): string {
    if (value == null) {
        return '';
    }

    return formatNumbersCompact(String(value));
}

/**
 * Generic AG Grid valueFormatter that displays USD currency.
 */
export function usdValueFormatter(params: ValueFormatterParams): string {
    return formatCurrencyUSD(params.value as NullableNumber);
}

/**
 * Generic AG Grid valueFormatter that displays percentages.
 */
export function percentageValueFormatter(params: ValueFormatterParams): string {
    return formatPercentageDisplay(params.value as NullableNumber);
}

/**
 * Generic AG Grid valueFormatter that displays simple numbers.
 */
export function numberValueFormatter(params: ValueFormatterParams): string {
    return formatNumberDisplay(params.value as NullableNumber);
}

/**
 * Factory to create a date valueFormatter using Angular's DatePipe.
 * Can be reused in any grid configuration.
 */
export function createDateValueFormatter(datePipe: DatePipe, format: string) {
    return (params: ValueFormatterParams): string => {
        const value = params.value;
        if (value == null || value === '') {
            return '';
        }

        const transformed = datePipe.transform(value, format);
        return transformed ?? '';
    };
}
